import { useCallback, useEffect, useRef, useState } from "react";
import {
  PracticeMethods,
  getAddNumArray,
  getCorrectAnswer,
  getDivNumArray,
  getMulNumArray,
} from "../utils/numbers.utils";

export interface GameConfig {
  practiceMethod: PracticeMethods;
  digits: number[];
  rows: number;
  numberOfSums: number;
  numberRange?: { min: number; max: number };
}

export interface GameQuestion {
  numbers: number[]; // Array of numbers in the question
  num1?: number; // For backward compatibility with display
  num2?: number; // For backward compatibility with display
  operator: "+" | "-" | "x" | "÷" | "";
  correctAnswer: number;
  answers: number[];
}

/**
 * Custom hook to manage game questions (math problems)
 * Handles generation of questions based on game configuration
 */
export const useGameQuestions = (gameConfig?: GameConfig) => {
  const [currentQuestion, setCurrentQuestion] = useState<GameQuestion | null>(
    null
  );

  // Ref to always access latest config (prevents stale closures when called before API loads)
  const gameConfigRef = useRef(gameConfig);
  useEffect(() => {
    gameConfigRef.current = gameConfig;
  }, [gameConfig]);

  // Generate plausible wrong answers for a given correct answer
  const generateAnswerChoices = useCallback(
    (correctAnswer: number, count: number = 2): number[] => {
      const answers = new Set<number>([correctAnswer]);
      let attempts = 0;
      const maxAttempts = 1000; // Safety limit to prevent infinite loops

      while (answers.size < count && attempts < maxAttempts) {
        attempts++;
        const variation = Math.floor(Math.random() * 5) + 1; // 1-5
        const method = Math.floor(Math.random() * 4);

        let wrongAnswer: number;
        switch (method) {
          case 0:
            wrongAnswer = correctAnswer + variation; // Add variation
            break;
          case 1:
            wrongAnswer = Math.max(1, correctAnswer - variation); // Subtract variation
            break;
          case 2:
            wrongAnswer = correctAnswer + (Math.random() > 0.5 ? 10 : -10); // Off by 10
            break;
          default:
            wrongAnswer = Math.max(
              1,
              correctAnswer + Math.floor(Math.random() * 20) - 10
            ); // Random nearby
        }

        if (wrongAnswer > 0 && wrongAnswer !== correctAnswer) {
          answers.add(wrongAnswer);
        }
      }

      // Fallback if max attempts reached
      if (attempts >= maxAttempts) {
        console.error(
          `[generateAnswerChoices] Hit max attempts, using fallback`
        );
        while (answers.size < count) {
          const fallbackAnswer = correctAnswer + answers.size * 10;
          if (fallbackAnswer > 0 && fallbackAnswer !== correctAnswer) {
            answers.add(fallbackAnswer);
          } else {
            break;
          }
        }
      }

      // Shuffle and return
      const answersArray = Array.from(answers);
      for (let i = answersArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [answersArray[i], answersArray[j]] = [answersArray[j], answersArray[i]];
      }
      return answersArray;
    },
    []
  );

  // Generate question based on game config (or use default if config not available)
  const generateQuestion = useCallback((): GameQuestion => {
    // Helper: Cap digit value at 3 to prevent unreasonably large numbers
    const capDigit = (digit: number): number => (digit > 3 ? 3 : digit);
    const config = gameConfigRef.current;

    // Default fallback: simple addition/subtraction
    if (!config) {
      const operator = Math.random() > 0.5 ? "+" : "-";
      let num1: number, num2: number, correctAnswer: number;

      if (operator === "+") {
        num1 = Math.floor(Math.random() * 50) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
        correctAnswer = num1 + num2;
      } else {
        num2 = Math.floor(Math.random() * 50) + 1;
        num1 = Math.floor(Math.random() * 50) + 1 + num2;
        correctAnswer = num1 - num2;
      }

      return {
        numbers: [num1, num2],
        num1,
        num2,
        operator,
        correctAnswer,
        answers: generateAnswerChoices(correctAnswer, 2),
      };
    }

    // Generate based on game configuration
    const { practiceMethod, digits, rows, numberRange } = config;
    let numbers: number[];
    let operator: "+" | "-" | "x" | "÷" | "" = "";

    // Cap digit values to prevent performance issues with very large numbers
    const digitValue = capDigit(Array.isArray(digits) ? digits[0] : digits);

    switch (practiceMethod) {
      case PracticeMethods.Addition:
        numbers = getAddNumArray(true, rows, digitValue, "add", numberRange);
        operator = "+";
        break;

      case PracticeMethods.AdditionSubtraction:
        numbers = getAddNumArray(false, rows, digitValue, "sub", numberRange);
        operator = "+";
        break;

      case PracticeMethods.Multiplication: {
        const mulDigit1 = capDigit(Array.isArray(digits) ? digits[0] : digits);
        const mulDigit2 = capDigit(
          Array.isArray(digits) ? digits[1] || mulDigit1 : mulDigit1
        );
        numbers = getMulNumArray(mulDigit1, mulDigit2, numberRange);
        operator = "x";
        break;
      }

      case PracticeMethods.Division: {
        const divDigit1 = capDigit(Array.isArray(digits) ? digits[0] : digits);
        const divDigit2 = capDigit(
          Array.isArray(digits) ? digits[1] || divDigit1 : divDigit1
        );
        numbers = getDivNumArray(divDigit1, divDigit2, numberRange);
        operator = "÷";
        break;
      }

      default:
        // Fallback to simple addition
        numbers = [
          Math.floor(Math.random() * 50) + 1,
          Math.floor(Math.random() * 10) + 1,
        ];
        operator = "+";
    }

    const correctAnswer = getCorrectAnswer(numbers, practiceMethod);

    return {
      numbers,
      num1: numbers[0],
      num2: numbers[1],
      operator,
      correctAnswer,
      answers: generateAnswerChoices(correctAnswer, 2),
    };
  }, [generateAnswerChoices]);

  // Generate new question and update state
  const generateNewQuestion = useCallback((): GameQuestion => {
    const question = generateQuestion();
    setCurrentQuestion(question);
    return question;
  }, [generateQuestion]);

  // Initialize with first question
  const initializeQuestion = useCallback((): GameQuestion => {
    return generateNewQuestion();
  }, [generateNewQuestion]);

  return {
    currentQuestion,
    generateQuestion,
    generateNewQuestion,
    initializeQuestion,
    setCurrentQuestion,
  };
};
